/*
Author: Chathuraka Mallawa Arachchi
Date: 18/10/2024
Copyright © 2024 Infix!. All rights reserved.

Responsible for form applying validations based on question type
*/

import { constants } from '@/constants'
import {
    AdvancedMultiChoiceCheckbox,
    DateRangeMatrixType,
    QuestionTextOnly,
    RepeatableMatrix,
    smokingType,
} from '@/constants/constants'
import { Utility } from '@/utils'
import ValidationManager from '../fieldValidations'
import nodeUtilities from '../nodeUtility'
import { FormState, IAnswers, IsmokingTypeValue } from '../types/commonTypes'
import { INode } from '../types/nodeTypes'
import { PatientAnswer, ValueTypes } from '../types/patientTypes'
import smokingTypes from './smokingType'

const patientQuestionnaireDetails = (function () {
    const generateQuestionnaireDetailsForm = (
        answer: PatientAnswer[],
        questionnaireDetails: INode,
        form: FormState
    ) => {
        const questionnair: INode = structuredClone(questionnaireDetails)

        const generateSections = (pageNode: INode) => {
            if (!nodeUtilities.isNodeTypeExists(pageNode.type)) {
                if (
                    pageNode.type === constants.section ||
                    pageNode.type === 'Section'
                ) {
                    //ToDo : incorrect section name. should be corrected from backend pageNode.type === constants.section
                    pageNode?.nodes?.map((pageNodeItem: INode) => {
                        const nodeObj = answer.find(
                            (item) => item.name === pageNodeItem.name
                        )
                        switch (pageNodeItem.type) {
                            case smokingType:
                                {
                                    const list: string[] = []
                                    let nodeList: INode[] = []

                                    if (Array.isArray(nodeObj?.value)) {
                                        const isSmokingTypeValue = (
                                            node: ValueTypes
                                        ): node is IsmokingTypeValue => {
                                            return (
                                                'type' in node &&
                                                'perDay' in node &&
                                                'years' in node
                                            )
                                        }
                                        nodeObj.value.forEach((node) => {
                                            if ('type' in node) {
                                                list.push(node.type) // Now TypeScript knows `node` has a `type` property
                                            }
                                            if (isSmokingTypeValue(node)) {
                                                const nodeListFromValue: INode[] =
                                                    smokingTypes.generateSmokingType(
                                                        node
                                                    )
                                                nodeList = [
                                                    ...nodeList,
                                                    ...nodeListFromValue,
                                                ]
                                            }
                                        })
                                        const smokeTypeIndex:
                                            | number
                                            | undefined =
                                            pageNode?.nodes &&
                                            pageNode?.nodes.findIndex(
                                                (pageNodeItem: INode) =>
                                                    pageNodeItem.name ===
                                                    smokingType
                                            )
                                        if (
                                            smokeTypeIndex !== -1 &&
                                            pageNode?.nodes &&
                                            smokeTypeIndex
                                        ) {
                                            pageNode.nodes?.splice(
                                                smokeTypeIndex + 1,
                                                0,
                                                ...nodeList
                                            )
                                        }
                                    }

                                    pageNodeItem.value = list
                                }
                                break
                            case DateRangeMatrixType:
                            case AdvancedMultiChoiceCheckbox:
                            case RepeatableMatrix:
                                {
                                    const formLocal: FormState = {}
                                    if (Array.isArray(nodeObj?.value)) {
                                        const validatedFormObjects =
                                            ValidationManager.applyCommonValidationsWithValue(
                                                pageNodeItem,
                                                JSON.stringify(nodeObj?.value)
                                            )

                                        formLocal[pageNodeItem.name] =
                                            validatedFormObjects
                                        form = { ...form, ...formLocal }
                                    } else {
                                        const validatedFormObjects =
                                            ValidationManager.applyCommonValidationsWithValue(
                                                pageNodeItem,
                                                ''
                                            )
                                        formLocal[pageNodeItem.name] =
                                            validatedFormObjects
                                        form = { ...form, ...formLocal }
                                    }
                                }
                                break
                            case QuestionTextOnly:
                                {
                                    if (pageNodeItem && pageNodeItem.nodes) {
                                        const subQuestionValues: IAnswers[] = []
                                        pageNodeItem.nodes.map(
                                            (questionTextNode) => {
                                                const nodeObj = answer.find(
                                                    (item) =>
                                                        item.name ===
                                                        questionTextNode.name
                                                )
                                                const formLocal: FormState = {}
                                                if (nodeObj?.value) {
                                                    const validatedFormObjects =
                                                        ValidationManager.applyCommonValidationsWithValue(
                                                            questionTextNode,
                                                            nodeObj?.value
                                                        )

                                                    formLocal[
                                                        questionTextNode.name
                                                    ] = validatedFormObjects
                                                    form = {
                                                        ...form,
                                                        ...formLocal,
                                                    }
                                                    const answer = {
                                                        name: questionTextNode.name,
                                                        value: nodeObj?.value as string,
                                                    }
                                                    subQuestionValues.push(
                                                        answer
                                                    )
                                                } else {
                                                    const validatedFormObjects =
                                                        ValidationManager.applyCommonValidationsWithValue(
                                                            questionTextNode,
                                                            ''
                                                        )
                                                    formLocal[
                                                        questionTextNode.name
                                                    ] = validatedFormObjects
                                                    const answer = {
                                                        name: questionTextNode.name,
                                                        value: '',
                                                    }
                                                    subQuestionValues.push(
                                                        answer
                                                    )
                                                    form = {
                                                        ...form,
                                                        ...formLocal,
                                                    }
                                                }
                                            }
                                        )
                                        const formLocal: FormState = {}
                                        const mainElementObject =
                                            ValidationManager.applyCommonValidationsWithValue(
                                                pageNodeItem,
                                                subQuestionValues
                                            )
                                        formLocal[pageNodeItem.name] =
                                            mainElementObject
                                        form = { ...form, ...formLocal }
                                    }
                                }
                                break
                            default: {
                                const formLocal: FormState = {}
                                if (nodeObj) {
                                    // pageNodeItem.value = nodeObj.value
                                    const validatedFormObjects =
                                        ValidationManager.applyCommonValidationsWithValue(
                                            pageNodeItem,
                                            nodeObj.value
                                        )
                                    formLocal[pageNodeItem.name] =
                                        validatedFormObjects
                                    form = { ...form, ...formLocal }
                                } else {
                                    const validatedFormObjects =
                                        ValidationManager.applyCommonValidationsWithValue(
                                            pageNodeItem,
                                            ''
                                        )
                                    formLocal[pageNodeItem.name] =
                                        validatedFormObjects
                                    form = { ...form, ...formLocal }
                                }
                            }
                        }
                    })
                }
            } else {
                pageNode?.nodes?.map((innerNode: INode) => {
                    generateSections(innerNode)
                })
            }
        }
        questionnair?.nodes?.map((innerNode: INode) => {
            generateSections(innerNode)
        })
        return form
    }

    const applyNextPrevLinksToANode = (
        node: INode,
        currentNode: INode,
        sectionName: string,
        form: FormState
    ) => {
        // const currentNode: INode= structuredClone(activeNode);
        //let prevSection: INode[] = [];
        let nextSection: INode[] | undefined = []

        const getNextSection = (index: number, innerNode: INode) => {
            //index - index of the current section of the loop
            if (innerNode.nodes && innerNode.nodes[index + 1]) {
                nextSection = innerNode.nodes[index + 1].nodes
            }
        }

        const loopThroughNodes = (pageNode: INode) => {
            if (!nodeUtilities.isNodeTypeExists(pageNode.type)) {
                if (
                    (pageNode.type === constants.section ||
                        pageNode.type === 'Section') &&
                    pageNode.name === sectionName &&
                    pageNode?.nodes
                ) {
                    // ToDo: section type shopuld be Section not section.
                    //prevSection =  pageNode?.nodes ? pageNode?.nodes : []
                    pageNode?.nodes?.map(
                        (pageNodeItem: INode, index: number) => {
                            if (
                                pageNodeItem.name === currentNode.name &&
                                pageNode.nodes
                            ) {
                                let nextNode: INode | undefined = undefined

                                if (!nextNode) {
                                    // next node without an expresssion
                                    let x = index + 1 // Start from the desired index
                                    while (x <= pageNode.nodes.length) {
                                        // for (let i = index+1; i < pageNode.nodes.length; i++) {
                                        const currentItem = pageNode.nodes[x]
                                        if (
                                            x === pageNode.nodes.length &&
                                            nextSection
                                        ) {
                                            currentNode.nextNode =
                                                nextSection[0].name
                                            break
                                        } else {
                                            if (!currentItem.showIf) {
                                                //nextNode = currentItem;
                                                currentNode.nextNode =
                                                    currentItem.name
                                                break
                                            } else {
                                                if (
                                                    Utility.evaluateExp(
                                                        currentItem,
                                                        form
                                                    )
                                                ) {
                                                    nextNode = currentItem
                                                    currentNode.nextNode =
                                                        currentItem.name
                                                    break
                                                }
                                            }
                                        }

                                        x++
                                    }
                                }
                                let i = index + 1 // Start from the desired index
                                while (i <= pageNode.nodes.length) {
                                    //for (let i = index+1; i < pageNode.nodes.length; i++) {
                                    const currentItem = pageNode.nodes[i]
                                    if (
                                        i === pageNode.nodes.length &&
                                        nextSection
                                    ) {
                                        currentNode.skipNode =
                                            nextSection[0].name
                                        break
                                    } else {
                                        if (!currentItem.showIf) {
                                            //nextNode = currentItem;
                                            currentNode.skipNode =
                                                currentItem.name
                                            break
                                        } else if (
                                            currentItem.showIf &&
                                            Utility.evaluateExp(
                                                currentItem,
                                                form
                                            )
                                        ) {
                                            currentNode.skipNode =
                                                currentItem.name
                                            break
                                        }
                                    }
                                    i++
                                }
                            }
                        }
                    )
                }
            } else {
                pageNode?.nodes?.map((innerNode: INode, index: number) => {
                    getNextSection(index, innerNode)
                    loopThroughNodes(innerNode)
                })
            }
        }
        node?.nodes?.map((innerNode: INode, index: number) => {
            getNextSection(index, node)
            loopThroughNodes(innerNode)
        })

        return currentNode
    }

    const findNearestSection = (
        paramNode: INode,
        targetName: string
    ): INode | null => {
        const node = structuredClone(paramNode)
        if (node.name === targetName) {
            return null
        }

        if (node.nodes) {
            const nodeList = [...node.nodes]
            for (const child of nodeList) {
                if (child.name === targetName) {
                    return node
                }
                const foundNode = findNearestSection(child, targetName)
                if (foundNode) {
                    return foundNode
                }
            }
        }

        return null
    }

    const findCurrentNode = (
        paramNode: INode,
        targetName: string
    ): INode | null => {
        const node = { ...paramNode }
        if (node.name === targetName) {
            return null
        }

        if (node.nodes) {
            const nodeList = [...node.nodes]
            for (const child of nodeList) {
                if (child.name === targetName) {
                    return child
                }
                const foundNode = findCurrentNode(child, targetName)
                if (foundNode) {
                    return foundNode
                }
            }
        }

        return null
    }

    const processText = (label: string, answers: PatientAnswer[]): string => {
        const updatedLabel = label.replace(/\{(\w+)\}/g, (_, key) => {
            const answer = answers.find((a) => a.name === key)

            // Replace only if the value is a string or can be safely converted to a string
            if (answer) {
                if (
                    typeof answer.value === 'string' ||
                    typeof answer.value === 'number' ||
                    typeof answer.value === 'boolean'
                ) {
                    return String(answer.value)
                } else {
                    // For arrays or objects, handle as needed
                    return JSON.stringify(answer.value) // or simply return `{${key}}` to skip
                }
            }

            // If no matching answer is found, return the placeholder as is
            return `{${key}}`
        })

        return updatedLabel
    }

    const getLastNode = (questionnaireDetails: INode): string => {
        if (questionnaireDetails && questionnaireDetails.nodes) {
            const lastSection =
                questionnaireDetails.nodes[
                    questionnaireDetails.nodes.length - 1
                ]
            if (lastSection && lastSection.nodes) {
                const lastNode = lastSection.nodes[lastSection.nodes.length - 1]
                return lastNode.name
            }
        }
        return ''
    }

    return {
        applyNextPrevLinksToANode,
        generateQuestionnaireDetailsForm,
        findNearestSection,
        findCurrentNode,
        getLastNode,
        processText,
    }
})()

export default patientQuestionnaireDetails
